// Admin settings page. First resident: the app-wide consultant list that
// fills the review screen's Consultant Summary dropdown — global on purpose,
// so updating a name or number never requires drafting and republishing a
// template. Edits autosave (same debounce pattern as the template builder).

import { useCallback, useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { AlignCenter, Contact, Plus, Trash2, UserCheck, Users } from "lucide-react";
import type { Consultant, FooterPreset, JdContact } from "@agp/shared";
import { useApi } from "@/hooks/useApi";
import { useMe } from "@/hooks/useMe";
import { Button } from "@/components/ui/button";
import { Card, CardBody, CardHeader, CardTitle } from "@/components/ui/card";
import { Input, Label } from "@/components/ui/input";
import { EmptyState } from "@/components/ui/empty-state";
import { StringListEditor } from "@/components/review/editors";

export function Settings() {
  const api = useApi();
  const me = useMe();
  const qc = useQueryClient();

  const listQuery = useQuery({
    queryKey: ["consultants"],
    queryFn: () => api.get<{ consultants: Consultant[] }>("/api/consultants"),
    enabled: me?.role === "admin",
  });

  const [consultants, setConsultants] = useState<Consultant[] | null>(null);
  const [saveState, setSaveState] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const saveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latest = useRef<Consultant[] | null>(null);

  useEffect(() => {
    if (listQuery.data && consultants === null) {
      setConsultants(listQuery.data.consultants);
      latest.current = listQuery.data.consultants;
    }
  }, [listQuery.data, consultants]);

  const persist = useCallback(async () => {
    if (!latest.current) return;
    setSaveState("saving");
    try {
      await api.patch(`/api/admin/consultants`, { consultants: latest.current });
      // The review screen shares this query key — refresh its dropdown.
      void qc.invalidateQueries({ queryKey: ["consultants"] });
      setSaveState("saved");
    } catch {
      setSaveState("error");
    }
  }, [api, qc]);

  const update = useCallback(
    (next: Consultant[]) => {
      setConsultants(next);
      latest.current = next;
      if (saveTimer.current) clearTimeout(saveTimer.current);
      saveTimer.current = setTimeout(() => void persist(), 800);
    },
    [persist],
  );

  // JD contacts — same autosave pattern as consultants.
  const jdContactsQuery = useQuery({
    queryKey: ["jd-contacts"],
    queryFn: () => api.get<{ contacts: JdContact[] }>("/api/jd-contacts"),
    enabled: me?.role === "admin",
  });
  const [jdContacts, setJdContacts] = useState<JdContact[] | null>(null);
  const jdContactsTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestJdContacts = useRef<JdContact[] | null>(null);

  useEffect(() => {
    if (jdContactsQuery.data && jdContacts === null) {
      setJdContacts(jdContactsQuery.data.contacts);
      latestJdContacts.current = jdContactsQuery.data.contacts;
    }
  }, [jdContactsQuery.data, jdContacts]);

  const persistJdContacts = useCallback(async () => {
    if (!latestJdContacts.current) return;
    setSaveState("saving");
    try {
      await api.patch(`/api/admin/jd-contacts`, { contacts: latestJdContacts.current });
      // The JD review screen's Contact tab shares this query key.
      void qc.invalidateQueries({ queryKey: ["jd-contacts"] });
      setSaveState("saved");
    } catch {
      setSaveState("error");
    }
  }, [api, qc]);

  const updateJdContacts = useCallback(
    (next: JdContact[]) => {
      setJdContacts(next);
      latestJdContacts.current = next;
      if (jdContactsTimer.current) clearTimeout(jdContactsTimer.current);
      jdContactsTimer.current = setTimeout(() => void persistJdContacts(), 800);
    },
    [persistJdContacts],
  );

  // Footer texts — same autosave pattern.
  const footerPresetsQuery = useQuery({
    queryKey: ["footer-presets"],
    queryFn: () => api.get<{ presets: FooterPreset[] }>("/api/footer-presets"),
    enabled: me?.role === "admin",
  });
  const [footerPresets, setFooterPresets] = useState<FooterPreset[] | null>(null);
  const footerPresetsTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestFooterPresets = useRef<FooterPreset[] | null>(null);

  useEffect(() => {
    if (footerPresetsQuery.data && footerPresets === null) {
      setFooterPresets(footerPresetsQuery.data.presets);
      latestFooterPresets.current = footerPresetsQuery.data.presets;
    }
  }, [footerPresetsQuery.data, footerPresets]);

  const persistFooterPresets = useCallback(async () => {
    if (!latestFooterPresets.current) return;
    setSaveState("saving");
    try {
      await api.patch(`/api/admin/footer-presets`, { presets: latestFooterPresets.current });
      // The builder and the review screen share this query key.
      void qc.invalidateQueries({ queryKey: ["footer-presets"] });
      setSaveState("saved");
    } catch {
      setSaveState("error");
    }
  }, [api, qc]);

  const updateFooterPresets = useCallback(
    (next: FooterPreset[]) => {
      setFooterPresets(next);
      latestFooterPresets.current = next;
      if (footerPresetsTimer.current) clearTimeout(footerPresetsTimer.current);
      footerPresetsTimer.current = setTimeout(() => void persistFooterPresets(), 800);
    },
    [persistFooterPresets],
  );

  // Candidate-owner names — same autosave pattern as consultants.
  const ownersQuery = useQuery({
    queryKey: ["candidate-owners"],
    queryFn: () => api.get<{ owners: string[] }>("/api/candidate-owners"),
    enabled: me?.role === "admin",
  });
  const [owners, setOwners] = useState<string[] | null>(null);
  const ownersTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const latestOwners = useRef<string[] | null>(null);

  useEffect(() => {
    if (ownersQuery.data && owners === null) {
      setOwners(ownersQuery.data.owners);
      latestOwners.current = ownersQuery.data.owners;
    }
  }, [ownersQuery.data, owners]);

  const persistOwners = useCallback(async () => {
    if (!latestOwners.current) return;
    setSaveState("saving");
    try {
      await api.patch(`/api/admin/candidate-owners`, { owners: latestOwners.current });
      // The review screen's owner dropdown shares this query key.
      void qc.invalidateQueries({ queryKey: ["candidate-owners"] });
      setSaveState("saved");
    } catch {
      setSaveState("error");
    }
  }, [api, qc]);

  const updateOwners = useCallback(
    (next: string[]) => {
      setOwners(next);
      latestOwners.current = next;
      if (ownersTimer.current) clearTimeout(ownersTimer.current);
      ownersTimer.current = setTimeout(() => void persistOwners(), 800);
    },
    [persistOwners],
  );

  if (me && me.role !== "admin") {
    return (
      <EmptyState
        icon={<Users className="h-5 w-5" strokeWidth={1.75} aria-hidden />}
        title="Admins only"
        body="Settings are managed by administrators."
      />
    );
  }

  const list = consultants ?? [];

  return (
    <div className="flex max-w-3xl flex-col gap-4">
      <div>
        <h2>Settings</h2>
        <p className="mt-1 text-[13.5px] text-text-2">
          {saveState === "saving"
            ? "Saving…"
            : saveState === "saved"
              ? "All changes saved"
              : saveState === "error"
                ? "Save failed — edits retry on next change"
                : "App-wide settings, applied everywhere immediately."}
        </p>
      </div>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Users size={16} strokeWidth={1.75} aria-hidden className="text-text-2" />
            <CardTitle>Consultants</CardTitle>
          </div>
        </CardHeader>
        <CardBody className="flex flex-col gap-2">
          {list.length === 0 && (
            <p className="text-[13px] text-text-2">
              No consultants yet — add the team below.
            </p>
          )}
          {list.map((c, i) => (
            <div key={i} className="grid grid-cols-[1fr_1fr_1fr_auto] items-center gap-2">
              <Input
                placeholder="Name"
                aria-label="Consultant name"
                value={c.name}
                onChange={(e) => {
                  const next = [...list];
                  next[i] = { ...c, name: e.target.value };
                  update(next);
                }}
              />
              <Input
                placeholder="Telephone"
                aria-label="Consultant telephone"
                value={c.telephone}
                onChange={(e) => {
                  const next = [...list];
                  next[i] = { ...c, telephone: e.target.value };
                  update(next);
                }}
              />
              <Input
                placeholder="Email"
                aria-label="Consultant email"
                value={c.email}
                onChange={(e) => {
                  const next = [...list];
                  next[i] = { ...c, email: e.target.value };
                  update(next);
                }}
              />
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                aria-label="Remove consultant"
                onClick={() => update(list.filter((_, j) => j !== i))}
              >
                <Trash2 size={14} />
              </Button>
            </div>
          ))}
          <Button
            variant="secondary"
            size="sm"
            className="self-start"
            onClick={() => update([...list, { name: "", telephone: "", email: "" }])}
          >
            <Plus size={14} /> Add consultant
          </Button>
          <p className="text-[12px] text-text-3">
            Shown as the &ldquo;Fill from consultant&rdquo; dropdown on the review screen&rsquo;s
            Consultant tab. Rows without a name are dropped on save.
          </p>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <Contact size={16} strokeWidth={1.75} aria-hidden className="text-text-2" />
            <CardTitle>JD contacts</CardTitle>
          </div>
        </CardHeader>
        <CardBody className="flex flex-col gap-3">
          {(jdContacts ?? []).length === 0 && (
            <p className="text-[13px] text-text-2">
              No JD contacts yet — add the people named in Please Contact blocks below.
            </p>
          )}
          {(jdContacts ?? []).map((c, i) => {
            const list = jdContacts ?? [];
            const patch = (p: Partial<JdContact>) => {
              const next = [...list];
              next[i] = { ...c, ...p };
              updateJdContacts(next);
            };
            return (
              <div key={i} className="flex flex-col gap-3 rounded-lg border border-border bg-surface p-4">
                <div className="grid grid-cols-[1fr_1fr_1fr_auto] items-end gap-2">
                  <div>
                    <Label>Name</Label>
                    <Input
                      value={c.name ?? ""}
                      aria-label="JD contact name"
                      onChange={(e) => patch({ name: e.target.value || null })}
                    />
                  </div>
                  <div>
                    <Label>Title</Label>
                    <Input
                      value={c.title ?? ""}
                      aria-label="JD contact title"
                      onChange={(e) => patch({ title: e.target.value || null })}
                    />
                  </div>
                  <div>
                    <Label>Company</Label>
                    <Input
                      value={c.company ?? ""}
                      aria-label="JD contact company"
                      onChange={(e) => patch({ company: e.target.value || null })}
                    />
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-9 w-7"
                    aria-label="Remove JD contact"
                    onClick={() => updateJdContacts(list.filter((_, j) => j !== i))}
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
                <div className="grid gap-3 sm:grid-cols-2">
                  {(
                    [
                      ["telephone", "Main phone"],
                      ["mobile", "Mobile"],
                      ["email", "Email"],
                      ["website", "Website"],
                    ] as const
                  ).map(([key, label]) => (
                    <div key={key}>
                      <Label>{label}</Label>
                      <StringListEditor
                        items={c[key] ?? []}
                        onChange={(values) => patch({ [key]: values })}
                      />
                    </div>
                  ))}
                </div>
              </div>
            );
          })}
          <Button
            variant="secondary"
            size="sm"
            className="self-start"
            onClick={() =>
              updateJdContacts([
                ...(jdContacts ?? []),
                {
                  name: null,
                  title: null,
                  company: null,
                  telephone: [],
                  mobile: [],
                  email: [],
                  website: [],
                },
              ])
            }
          >
            <Plus size={14} /> Add JD contact
          </Button>
          <p className="text-[12px] text-text-3">
            Typing or picking one of these names on a job description&rsquo;s Contact tab fills
            the whole Please Contact block. Rows without a name are dropped on save.
          </p>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <UserCheck size={16} strokeWidth={1.75} aria-hidden className="text-text-2" />
            <CardTitle>Candidate owners</CardTitle>
          </div>
        </CardHeader>
        <CardBody className="flex flex-col gap-2">
          {(owners ?? []).length === 0 && (
            <p className="text-[13px] text-text-2">
              No owners yet — reviewers can add names from the review screen, or add them here.
            </p>
          )}
          {(owners ?? []).map((name, i) => (
            <div key={i} className="grid grid-cols-[1fr_auto] items-center gap-2">
              <Input
                placeholder="Name"
                aria-label="Candidate owner name"
                value={name}
                onChange={(e) => {
                  const next = [...(owners ?? [])];
                  next[i] = e.target.value;
                  updateOwners(next);
                }}
              />
              <Button
                variant="ghost"
                size="icon"
                className="h-7 w-7"
                aria-label="Remove candidate owner"
                onClick={() => updateOwners((owners ?? []).filter((_, j) => j !== i))}
              >
                <Trash2 size={14} />
              </Button>
            </div>
          ))}
          <Button
            variant="secondary"
            size="sm"
            className="self-start"
            onClick={() => updateOwners([...(owners ?? []), ""])}
          >
            <Plus size={14} /> Add owner
          </Button>
          <p className="text-[12px] text-text-3">
            Offered in the Candidate owner dropdown on the review screen (together with the
            consultant names above). Fix typos or remove old names here — renaming does not
            retag resumes already saved with the old spelling.
          </p>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <div className="flex items-center gap-2">
            <AlignCenter size={16} strokeWidth={1.75} aria-hidden className="text-text-2" />
            <CardTitle>Footer texts</CardTitle>
          </div>
        </CardHeader>
        <CardBody className="flex flex-col gap-3">
          {(footerPresets ?? []).length === 0 && (
            <p className="text-[13px] text-text-2">
              No footer texts yet — add one per office or entity below.
            </p>
          )}
          {(footerPresets ?? []).map((p, i) => {
            const list = footerPresets ?? [];
            const patch = (next: Partial<FooterPreset>) => {
              const copy = [...list];
              copy[i] = { ...p, ...next };
              updateFooterPresets(copy);
            };
            return (
              <div key={i} className="flex flex-col gap-3 rounded-lg border border-border bg-surface p-4">
                <div className="grid grid-cols-[1fr_auto] items-end gap-2">
                  <div>
                    <Label>Name</Label>
                    <Input
                      value={p.name}
                      aria-label="Footer text name"
                      placeholder="e.g. Tokyo office"
                      onChange={(e) => patch({ name: e.target.value })}
                    />
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-9 w-7"
                    aria-label="Remove footer text"
                    onClick={() => updateFooterPresets(list.filter((_, j) => j !== i))}
                  >
                    <Trash2 size={14} />
                  </Button>
                </div>
                <div>
                  <Label>Lines</Label>
                  <StringListEditor
                    items={p.lines}
                    onChange={(lines) => patch({ lines })}
                    placeholder="Footer line"
                  />
                </div>
              </div>
            );
          })}
          <Button
            variant="secondary"
            size="sm"
            className="self-start"
            onClick={() => updateFooterPresets([...(footerPresets ?? []), { name: "", lines: [] }])}
          >
            <Plus size={14} /> Add footer text
          </Button>
          <p className="text-[12px] text-text-3">
            A template picks one of these as its default footer in the builder; the review
            screen can switch a resume to any other. Up to four lines each. Rows without a
            name are dropped on save.
          </p>
        </CardBody>
      </Card>
    </div>
  );
}
